import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { listStudents, updateStudentStatus, deleteStudent } from "../../api/studentApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { createLookupApi } from "../../api/lookupApi";
import "../Users/UsersPage.css";
import "./Students.css";

const academicYearApi = createLookupApi("academic-years");

export default function StudentsListPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [classConfigurations, setClassConfigurations] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([academicYearApi.list(), listClassConfigurations()]).then(([years, configs]) => {
      setAcademicYears(years);
      setClassConfigurations(configs);
      if (years.length > 0) {
        setAcademicYear(years[0].name);
      }
    });
  }, []);

  useEffect(() => {
    if (!academicYear) {
      return;
    }
    setLoading(true);
    listStudents(academicYear, classConfigurationId || undefined)
      .then(setStudents)
      .catch(() => setError("Failed to load students."))
      .finally(() => setLoading(false));
  }, [academicYear, classConfigurationId]);

  const handleToggleStatus = async (student) => {
    setError("");
    try {
      await updateStudentStatus(student.studentId, { active: !student.studentStatus });
      setSuccess(`${student.studentName} ${student.studentStatus ? "disabled" : "enabled"}.`);
      setStudents((prev) =>
        prev.map((s) => (s.studentId === student.studentId ? { ...s, studentStatus: !s.studentStatus } : s))
      );
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update status.");
    }
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Delete student "${student.studentName}" (${student.customStudentId})?`)) {
      return;
    }
    setError("");
    try {
      await deleteStudent(student.studentId);
      setSuccess("Student deleted.");
      setStudents((prev) => prev.filter((s) => s.studentId !== student.studentId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete student.");
    }
  };

  const columns = [
    { key: "customStudentId", label: "ID" },
    { key: "studentName", label: "Name" },
    { key: "classConfigName", label: "Class" },
    { key: "studentRoll", label: "Roll" },
    { key: "guardianMobile", label: "Guardian Mobile" },
    { key: "studentStatus", label: "Status", render: (row) => (row.studentStatus ? "Active" : "Disabled") },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Students</h1>
        <Link className="btn-primary" to="/students/new">
          + Add Student
        </Link>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="student-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            <option value="">All classes</option>
            {classConfigurations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={students}
          emptyMessage="No students found for this academic year/class."
          actions={(student) => (
            <>
              <Link className="btn-link" to={`/students/${student.studentId}/edit`}>
                Edit
              </Link>
              <button type="button" className="btn-link" onClick={() => handleToggleStatus(student)}>
                {student.studentStatus ? "Disable" : "Enable"}
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(student)}>
                Delete
              </button>
            </>
          )}
        />
      )}
    </MainLayout>
  );
}
